import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import RoleEditModal from './RoleEditModal';

const PAGE_SIZE = 15;

const EMPTY_FILTERS = {
  role: '',
};

const TABLE_COLUMNS = ['角色名稱', '創建人', '創建時間', '操作'];

function buildPageParams(pageNo, filters) {
  return {
    pageNo,
    pageSize: PAGE_SIZE,
    ...(filters.role?.trim() ? { role: filters.role.trim() } : {}),
  };
}

export default function RoleManagePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function loadPermissions() {
    setPermissionsLoading(true);
    try {
      const data = await adminApi.listAdminPermissions();
      setPermissions(Array.isArray(data) ? data : []);
    } catch (err) {
      setPermissions([]);
      setError(err.message || '載入權限樹失敗');
    } finally {
      setPermissionsLoading(false);
    }
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.adminRolePage(buildPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateAdminRole(payload);
        setMessage('已更新');
      } else {
        await adminApi.createAdminRole(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.removeAdminRoles([deleteTarget.id]);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>角色管理</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增
        </button>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="roleFilter">角色名稱</label>
          <input
            id="roleFilter"
            type="text"
            value={filters.role}
            onChange={(e) => updateFilter('role', e.target.value)}
            placeholder="請輸入角色名稱"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && <p className="page-desc">共 {total} 筆</p>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.role}</td>
                  <td>{row.createUserName || '—'}</td>
                  <td>{row.createTime ? formatDateTime(row.createTime) : '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={row.id === 1}
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <RoleEditModal
        open={editOpen}
        row={editRow}
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除角色"
        message={deleteTarget ? `確定刪除角色「${deleteTarget.role}」？` : ''}
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
